import { type KeyboardEvent, useState } from "react"
import { personInitials } from "../../computers/people"
import { useOptionalCodeStore } from "../../store/context"
import { PersonGlyph } from "../computers/PersonGlyph"

/**
 * Who you are to the people you work with. The name is the headline and
 * edits in place; there is no form, no save button. Blur or Enter commits.
 */
export function IdentityHero() {
    const codeStore = useOptionalCodeStore()
    const settings = codeStore?.personalSettingsStore?.settings
    const [name, setName] = useState(() => settings?.current.profile?.name ?? "")
    const [glyph, setGlyph] = useState(() => settings?.current.profile?.glyph ?? "")
    if (!settings || codeStore?.isGuest) return null
    const color = settings.current.profile?.color

    const save = () => {
        const trimmedName = name.trim()
        const trimmedGlyph = glyph.trim().slice(0, 2)
        const profile = {
            ...(trimmedName ? { name: trimmedName } : {}),
            ...(trimmedGlyph ? { glyph: trimmedGlyph } : {}),
            ...(color ? { color } : {}),
        }
        settings.set({ profile: Object.keys(profile).length > 0 ? profile : undefined })
    }

    const commitOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault()
            event.currentTarget.blur()
        }
    }

    return (
        <div className="sharing-rise flex items-center gap-5 border border-border bg-base-200 p-5 max-[600px]:flex-wrap">
            <PersonGlyph name={name.trim() || "You"} glyph={glyph} color={color} size="xl" />
            <div className="min-w-0 flex-1">
                <input
                    className="input -mx-2 w-[calc(100%+16px)] bg-transparent px-2 py-1 text-xl font-semibold tracking-tight text-base-content outline-none transition-colors placeholder:text-muted/60 hover:bg-base-300/40 focus:bg-base-100"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    onBlur={save}
                    onKeyDown={commitOnEnter}
                    placeholder="Your name"
                    aria-label="Your name"
                    maxLength={80}
                    autoComplete="off"
                />
                <p className="mt-1 text-sm text-muted">How you appear to the people you work with.</p>
            </div>
            <label className="flex flex-none flex-col items-end gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">Initials</span>
                <input
                    className="input h-10 w-16 border border-border bg-input text-center text-sm font-semibold uppercase tracking-wider text-base-content outline-none placeholder:font-normal placeholder:text-muted/50 focus:border-primary"
                    value={glyph}
                    onChange={(event) => setGlyph(event.target.value.slice(0, 2))}
                    onBlur={save}
                    onKeyDown={commitOnEnter}
                    placeholder={personInitials(name.trim() || "You")}
                    aria-label="Your initials"
                    maxLength={2}
                    autoComplete="off"
                />
                <span className="text-[10px] text-muted">{glyph.trim() ? "Custom" : "Automatic"}</span>
            </label>
        </div>
    )
}
